// 카탈로그 파라미터 지정의 **저작 UI** — 전역 편집기(양식 탭)와 바인딩 자리 인라인 블록이 공유한다.
//
// 저장 자리는 한 곳뿐이다: `ReportTemplate.catalogParams[catalogKey][paramName]`
// (결정 2026-08-06-catalog-param-binding-axis 개정 1). 이 파일이 더하는 것은 **편집 진입점**이지
// 새 저장소가 아니다 — 바인딩 에디터에서 고친 값과 전역 편집기에서 고친 값은 같은 값이다.
//
// 순수 로직(행 산출·후보 분류·요약 문구)은 `catalogParamBindings.ts`에 있고, 여기엔 렌더와
// 편집 채널(Context)만 둔다.

import { createContext, useContext } from 'react';
import type {
  CatalogEntry,
  CatalogParamResolutionMap,
  ParamBinding,
  TemplateCatalogParamBindings,
  TemplateVariable,
} from '@safetyhub/shared';
import {
  buildCatalogParamItems,
  catalogEntryParams,
  catalogParamAbsentNote,
  catalogParamBindingSummary,
  catalogParamEffectiveNote,
  paramBindingMode,
  paramVariableCandidates,
  type CatalogParamRowItem,
  type ParamBindingMode,
} from './catalogParamBindings';

/**
 * 파라미터 지정 편집 채널 — 바인딩 에디터가 "그 자리에서" 파라미터를 고칠 수 있게 흘린다.
 *
 * prop으로 내리지 않는 이유는 `PreviewValuesContext`와 같다: `BindingEditor` 호출부 다섯 자리
 * (노드 속성 패널·표 셀·일괄 바인딩 draft·계산 입력 '직접 지정'·분기 조건 키)가 전부 속성 패널
 * 여러 겹 아래라, 중간 컴포넌트 하나만 빠뜨리면 **그 화면에서만 조용히 안 뜬다**.
 *
 * 값이 없으면(Provider 밖) 인라인 블록은 그냥 렌더되지 않는다 — 전역 편집기는 그대로 있으므로
 * 편집 수단이 사라지는 게 아니라 진입점 하나가 없을 뿐이다.
 */
export interface CatalogParamsEditChannel {
  /** 현재 템플릿의 지정 전체(`ReportTemplate.catalogParams`). */
  bindings?: TemplateCatalogParamBindings;
  /** 변수 참조 후보 — 전역 편집기와 같은 목록. */
  variables: TemplateVariable[];
  /**
   * 미리보기 응답의 파라미터 해소 스탬프 — "그래서 지금 실제로 무슨 값이 쓰이나"의 원료.
   * 새 데이터 채널을 뚫지 않고 이미 오는 `previewResult.paramResolution`을 그대로 흘린다.
   */
  resolution?: CatalogParamResolutionMap;
  /** 실데이터 미리보기가 반영된 상태인가 — false면 값을 지어내지 않고 안내로 degrade한다. */
  hasPreviewContext?: boolean;
  /**
   * 해소 스탬프의 `effective.id`에 붙일 이름 — 호출부가 이미 받아 둔 목록 행에서 조인한다
   * (`catalogEffectiveIdName`). 모르면 undefined를 돌려주면 화면이 raw id로 degrade한다.
   */
  effectiveNameOf?: (catalogKey: string, id: string) => string | undefined;
  /**
   * 미리보기 컨텍스트 고르기로 진입 — 컨텍스트가 없을 때 안내 문구 옆 버튼이 부른다.
   * 안내만 있고 갈 수단이 없으면 저작자가 그 자리를 찾아 헤맨다(2026-08-10 P5).
   */
  onPickPreviewContext?: () => void;
  /** 지정 갱신(undefined = 해제). 내부에서 `setCatalogParamBinding`으로 정규화해 템플릿에 반영한다. */
  setBinding: (
    catalogKey: string,
    paramName: string,
    binding: ParamBinding | undefined,
  ) => void;
}

export const CatalogParamsEditContext = createContext<CatalogParamsEditChannel | undefined>(
  undefined,
);

/**
 * 조회 한 건의 **해소 결과** 한 줄 — 지금 이 컨텍스트에서 실제로 낙점된 대상.
 *
 * 파라미터가 아니라 **조회(카탈로그) 단위**로 그린다: 스탬프의 `effective`가 "그 조회가 무엇을
 * 낙점했나"라 파라미터마다 복제하면 같은 문장이 N번 뜨고, 파라미터가 둘 이상인 조회에서는
 * 어느 파라미터의 결과인지 거짓으로 읽힌다. 전역 편집기와 인라인 블록이 같은 컴포넌트를 쓴다.
 */
export function CatalogParamEffectiveNote({
  catalogKey,
  resolution,
  hasPreviewContext,
}: {
  catalogKey: string;
  resolution?: CatalogParamResolutionMap;
  hasPreviewContext?: boolean;
}) {
  // 이름 조인과 컨텍스트 진입은 **채널에서** 읽는다(prop으로 내리지 않는 이유는 위 채널 JSDoc과
  // 같다 — 이 컴포넌트는 전역 편집기와 인라인 블록 두 자리에서 쓰이고, 둘 다 Provider 안이다).
  const channel = useContext(CatalogParamsEditContext);
  const effective = resolution?.[catalogKey]?.effective;
  const effectiveId = effective?.id;
  const effectiveName =
    effectiveId !== undefined ? channel?.effectiveNameOf?.(catalogKey, effectiveId) : undefined;
  const note = catalogParamEffectiveNote(effective, hasPreviewContext === true, effectiveName);
  return (
    <p className="text-[11px] leading-snug text-gray-400">
      {note.text}
      {hasPreviewContext !== true && channel?.onPickPreviewContext && (
        <button
          type="button"
          onClick={channel.onPickPreviewContext}
          className="ml-1 rounded border border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50"
        >
          컨텍스트 고르기
        </button>
      )}
    </p>
  );
}

/** 파라미터 한 개의 지정 행 — 저작 대상('user')이면 편집 컨트롤, 아니면 읽기 전용 한 줄. */
export function CatalogParamBindingRow({
  item,
  variables,
  onSetBinding,
}: {
  item: CatalogParamRowItem;
  variables: TemplateVariable[];
  /** undefined = 지정 해제(카탈로그 기본 해소로 되돌림). */
  onSetBinding: (binding: ParamBinding | undefined) => void;
}) {
  const { param, binding } = item;
  const summary = catalogParamBindingSummary(param, binding, variables);
  const absentNote = catalogParamAbsentNote(item);

  // context/const — 카탈로그 선언만으로 결정되는 축이라 편집 대상이 아니다. 다만 감추지 않는다.
  if (!item.authorable) {
    return (
      <div className="flex items-baseline justify-between gap-2 rounded bg-gray-50 px-2 py-1">
        <span className="min-w-0 truncate text-[11px] text-gray-600">
          {param.label || param.name}
          <span className="ml-1 font-mono text-[10px] text-gray-400">{param.name}</span>
        </span>
        <span className="shrink-0 text-[11px] text-gray-500">{summary}</span>
      </div>
    );
  }

  const candidates = paramVariableCandidates(param, variables);
  const mode = paramBindingMode(binding);
  const literalValue = binding?.from === 'literal' ? binding.value : undefined;
  const variableKey = binding?.from === 'variable' ? binding.key : '';
  const noVariables = variables.length === 0;
  const varOption = (v: TemplateVariable) => (
    <option key={v.key} value={v.key}>
      {v.label || v.key} ({v.key})
    </option>
  );

  const changeMode = (next: ParamBindingMode) => {
    if (next === 'none') return onSetBinding(undefined);
    if (next === 'literal') return onSetBinding({ from: 'literal', value: '' });
    // 권장 후보를 먼저 집는다 — 없으면 아무 변수나(그래도 선택은 바꿀 수 있다).
    const first = candidates.recommended[0] ?? candidates.others[0];
    if (!first) return;
    onSetBinding({ from: 'variable', key: first.key });
  };

  return (
    <div className="space-y-1.5 rounded border border-gray-100 bg-gray-50 p-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 truncate text-[11px] font-medium text-gray-700">
          {param.label || param.name}
          {/* 필수 = 카탈로그가 `validation.required`로 "폴백 없음"을 선언한 것. */}
          {param.validation?.required && (
            <span className="ml-1 rounded bg-red-50 px-1 text-[10px] text-red-600">필수</span>
          )}
        </span>
        <span className="shrink-0 font-mono text-[10px] text-gray-400">{param.name}</span>
      </div>

      <div className="flex items-center gap-1">
        <select
          value={mode}
          onChange={(e) => changeMode(e.target.value as ParamBindingMode)}
          className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-1.5 py-1 text-xs"
        >
          <option value="none">
            {param.validation?.required ? '미지정(값 없음)' : '미지정(기본 조회 방식)'}
          </option>
          <option value="literal">직접 입력</option>
          <option value="variable" disabled={noVariables}>
            전역 변수{noVariables ? ' (변수 없음)' : ''}
          </option>
        </select>
        {binding && (
          <button
            type="button"
            onClick={() => onSetBinding(undefined)}
            className="shrink-0 rounded border border-gray-300 px-1.5 py-1 text-[11px] text-gray-600 hover:bg-white"
            title="지정을 지우고 미지정 상태로 되돌립니다(그때의 동작은 아래 안내 참조)."
          >
            지정 해제
          </button>
        )}
      </div>

      {mode === 'literal' &&
        (param.widget === 'checkbox' ? (
          <label className="flex items-center gap-1.5 text-[11px] text-gray-600">
            <input
              type="checkbox"
              checked={Boolean(literalValue)}
              onChange={(e) => onSetBinding({ from: 'literal', value: e.target.checked })}
            />
            참(true)
          </label>
        ) : (
          <input
            type={param.widget === 'number' ? 'number' : 'text'}
            value={literalValue === undefined || literalValue === null ? '' : String(literalValue)}
            onChange={(e) => {
              const raw = e.target.value;
              onSetBinding({
                from: 'literal',
                value: param.widget === 'number' && raw !== '' ? Number(raw) : raw,
              });
            }}
            className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
            placeholder="값"
          />
        ))}

      {mode === 'variable' && (
        <select
          value={variableKey}
          onChange={(e) => onSetBinding({ from: 'variable', key: e.target.value })}
          className="w-full min-w-0 rounded border border-gray-300 bg-white px-1.5 py-1 text-xs"
        >
          {/* 저장된 지정이 목록 밖(삭제·리네임된 변수)이어도 표시 유지 — silent 소실 방지 */}
          {item.danglingVariable && <option value={variableKey}>{variableKey} (없는 변수)</option>}
          {candidates.hasHint ? (
            <>
              {candidates.recommended.length > 0 && (
                <optgroup label="권장 — 이 파라미터가 받는 형상과 일치">
                  {candidates.recommended.map(varOption)}
                </optgroup>
              )}
              {candidates.others.length > 0 && (
                <optgroup label="그 외 변수 — 선택 가능">
                  {candidates.others.map(varOption)}
                </optgroup>
              )}
            </>
          ) : (
            candidates.others.map(varOption)
          )}
        </select>
      )}

      <p className="text-[11px] leading-snug text-gray-500">{summary}</p>

      {/* 같은 "지정 있음"이라도 값의 유무로 결과가 갈린다 — 한 문장에 뭉치면 서로 어긋나 읽힌다
          (2026-08-10 사용자 지적). 부재 시 결과 문장은 조회의 `whenAbsent`/`required` 선언에서
          오고, 이 컴포넌트는 특정 조회의 규칙을 알지 않는다(결정 2026-08-10 §2). */}
      {absentNote && (
        <p
          className={`text-[11px] leading-snug ${absentNote.tone === 'warn' ? 'text-amber-600' : 'text-gray-400'}`}
        >
          {absentNote.text}
        </p>
      )}
      {item.danglingVariable && (
        <p className="text-[11px] leading-snug text-amber-600">
          지정한 전역 변수가 이 양식에 없습니다 — 값이 비어 미지정과 같게 처리됩니다.
        </p>
      )}
      {item.unknownParam && (
        <p className="text-[11px] leading-snug text-amber-600">
          시스템 조회가 선언하지 않은 파라미터입니다(제거·리네임) — 해소에 쓰이지 않으니 해제를
          권합니다.
        </p>
      )}
      {mode === 'literal' && param.widget === 'select' && (
        <p className="text-[11px] leading-snug text-gray-400">
          선택형 파라미터지만 시스템 조회가 고정 옵션을 선언하지 않아 값을 직접 적습니다.
        </p>
      )}
    </div>
  );
}

/**
 * 바인딩 에디터 하단의 파라미터 편집 블록 — "출처를 고른 그 자리에서" 파라미터를 채운다.
 *
 * 저작자 요구는 "바인딩할 때 그 자리에서 지정"이었고, 저장 자리를 옮기자는 게 아니었다. 그래서
 * 값은 전역 편집기와 **같은 곳**(`template.catalogParams`)에 쓰고, 그 사실을 캡션으로 못박는다 —
 * 이 자리만의 값이라고 오해하면 다른 자리에서 바뀌는 걸 버그로 읽는다. 캡션은 전역 섹션의 부제와
 * **서로를 가리키는 짝**이다(한쪽만 알면 "어디가 진짜인가"가 된다).
 *
 * 일상 편집은 여기가 맡고, 전역 섹션은 전체 조망 + 바인딩 에디터가 없는 참조(변수 `prefillFrom`·
 * 셀 `rangesFrom` 같은 단수 entry 직접 참조)의 편집처로 남는다.
 *
 * 저작 대상(`provenance:'user'`)이 하나도 없으면 렌더하지 않는다. 대신 저작 대상이 있는 카탈로그
 * **안에서는** 자동으로 채워지는 파라미터도 한 줄로 보여준다 — 무엇이 저절로 들어가는지 모르면
 * 지정의 의미를 판단할 수 없다(전역 편집기의 표시 규율과 같다. 폭이 좁아 요약 한 줄로 압축).
 */
export function CatalogParamInlineBlock({
  catalogKey,
  entry,
}: {
  /** 현재 바인딩이 가리키는 카탈로그 키(없으면 미렌더). */
  catalogKey: string | undefined;
  /** 그 키의 카탈로그 entry — 파라미터 선언의 출처. entry가 없으면 제목은 키로 폴백. */
  entry: CatalogEntry | undefined;
}) {
  const channel = useContext(CatalogParamsEditContext);
  // Provider 밖이거나(편집 채널 없음) 바인딩이 카탈로그를 가리키지 않으면 계산할 것도 없다.
  if (!channel || !catalogKey) return null;

  const items = buildCatalogParamItems(
    catalogEntryParams(entry),
    channel.bindings?.[catalogKey] ?? {},
    channel.variables ?? [],
  );
  const authorable = items.filter((item) => item.authorable);
  const automatic = items.filter((item) => !item.authorable);

  // 저작할 파라미터가 없으면 블록 자체를 만들지 않는다(자동 파라미터만 있는 카탈로그).
  if (authorable.length === 0) return null;

  return (
    <div className="space-y-1.5 rounded border border-brand-100 bg-brand-50/40 p-2">
      {/* 제목에 어느 카탈로그인지 명시 — 한 패널에 블록이 2개 병립할 수 있어(rangesFrom 등)
          "이 출처"만으로는 어느 출처인지 구분이 안 된다. entry가 없으면(제거·리네임) 키로 폴백. */}
      <div className="text-[11px] font-semibold text-brand-700">
        이 시스템 조회({entry?.label ?? catalogKey})에 전달할 파라미터
      </div>
      <p className="text-[11px] leading-snug text-gray-500">
        이 설정은 문서 전체에 적용됩니다 (전역 설정의 조회 파라미터와 같은 값).
      </p>
      {automatic.length > 0 && (
        <p className="text-[11px] leading-snug text-gray-400">
          자동:{' '}
          {automatic
            .map(
              (item) =>
                `${item.param.name} ← ${catalogParamBindingSummary(
                  item.param,
                  undefined,
                  channel.variables,
                )}`,
            )
            .join(' · ')}
        </p>
      )}
      <CatalogParamEffectiveNote
        catalogKey={catalogKey}
        resolution={channel.resolution}
        hasPreviewContext={channel.hasPreviewContext}
      />
      {authorable.map((item) => (
        <CatalogParamBindingRow
          key={item.param.name}
          item={item}
          variables={channel.variables}
          onSetBinding={(binding) => channel.setBinding(catalogKey, item.param.name, binding)}
        />
      ))}
    </div>
  );
}
